import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { profitBudgetAPI } from '../../lib/api';
import {
  Target,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  RefreshCw,
  ChevronDown,
  Check,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
} from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatPercent = (value) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

// Variance Badge Component
function VarianceBadge({ variance, percent }) {
  const isFavorable = variance >= 0;
  
  return (
    <div className={`flex items-center gap-1 text-xs font-medium ${
      isFavorable ? 'text-green-600' : 'text-red-600'
    }`}>
      {isFavorable ? (
        <TrendingUp className="h-3 w-3" />
      ) : (
        <TrendingDown className="h-3 w-3" />
      )}
      <span>{formatCurrency(Math.abs(variance))}</span>
      {percent !== undefined && (
        <span className="text-muted-foreground">({formatPercent(percent)})</span>
      )}
    </div>
  );
}

// Performance Row Component
function PerformanceRow({ account, showMonthly }) {
  const isFavorable = account.annual_variance >= 0;
  
  return (
    <>
      <tr className={`border-b border-border hover:bg-muted/30 ${
        !isFavorable ? 'bg-red-50/30' : ''
      }`} data-testid={`performance-row-${account.account_id}`}>
        {/* Account Name */}
        <td className="py-3 px-3 sticky left-0 bg-card z-10 min-w-[180px]">
          <span className="font-medium text-sm">{account.account_name}</span>
        </td>

        {/* Annual Budget */}
        <td className="py-3 px-3 text-right font-mono text-sm bg-blue-50/50">
          {formatCurrency(account.annual_budget)}
        </td>

        {/* Annual Actual */}
        <td className="py-3 px-3 text-right font-mono text-sm bg-green-50/50">
          {formatCurrency(account.annual_actual)}
        </td>

        {/* Annual Variance */}
        <td className={`py-3 px-3 text-right font-mono text-sm ${
          isFavorable ? 'text-green-600 bg-green-50/30' : 'text-red-600 bg-red-50/30'
        }`}>
          {isFavorable ? '+' : ''}{formatCurrency(account.annual_variance)}
          <span className="text-xs ml-1">({formatPercent(account.variance_percent)})</span>
        </td>

        {/* Monthly Columns (if expanded) */}
        {showMonthly && MONTHS.map((_, idx) => {
          const month = idx + 1;
          const budget = account.monthly_budget?.[month] || 0;
          const actual = account.monthly_actual?.[month] || 0;
          const variance = account.monthly_variance?.[month] || 0;
          const isMonthFavorable = variance >= 0;
          
          return (
            <td key={month} className="py-3 px-2 text-center min-w-[90px]">
              <div className="text-xs">
                <div className="text-muted-foreground">{formatCurrency(budget)}</div>
                <div className={isMonthFavorable ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                  {formatCurrency(actual)}
                </div>
                <div className={`text-xs ${isMonthFavorable ? 'text-green-600' : 'text-red-600'}`}>
                  {isMonthFavorable ? '+' : ''}{formatCurrency(variance)}
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    </>
  );
}

// Summary Card Component
function SummaryCard({ title, value, subValue, icon: Icon, color, trend }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold ${color || ''}`}>{value}</p>
          {subValue && <p className="text-sm text-muted-foreground mt-0.5">{subValue}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${color?.includes('green') ? 'bg-green-100' : color?.includes('red') ? 'bg-red-100' : 'bg-primary/10'}`}>
            <Icon className={`h-5 w-5 ${color || 'text-primary'}`} />
          </div>
        )}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          <span>{formatPercent(trend)} vs budget</span>
        </div>
      )}
    </div>
  );
}

// Year Selector
function YearSelector({ years, selectedYearId, onSelect }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const selectedYear = years?.find(y => y.id === selectedYearId);

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted"
        data-testid="year-selector"
      >
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">
          {selectedYear ? `${selectedYear.name} (${selectedYear.year})` : 'Select Budget Year'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-lg z-20">
          <div className="p-2 border-b border-border">
            <p className="text-xs text-muted-foreground uppercase font-medium px-2">Select Year to Compare</p>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {years?.map(y => (
              <button
                key={y.id}
                onClick={() => {
                  onSelect(y.id);
                  setShowDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left hover:bg-muted flex items-center justify-between ${
                  y.id === selectedYearId ? 'bg-primary/10' : ''
                }`}
              >
                <div>
                  <p className="font-medium">{y.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {y.year} • {formatCurrency(y.total_budget)} budgeted
                  </p>
                </div>
                {y.id === selectedYearId && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
            
            {(!years || years.length === 0) && (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                No budget years available. Create one in Budget Planning first.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Budget Performance Page
export default function BudgetPerformance() {
  const { selectedCompany } = useCompany();
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [showMonthly, setShowMonthly] = useState(false);

  // Fetch budget years
  const { data: years = [], isLoading: yearsLoading } = useQuery({
    queryKey: ['budget-years', selectedCompany?.id],
    queryFn: () => profitBudgetAPI.getYears(selectedCompany?.id).then(res => res.data),
    enabled: !!selectedCompany?.id,
  });

  // Auto-select first year
  useEffect(() => {
    if (years.length > 0 && !selectedYearId) {
      setSelectedYearId(years[0].id);
    }
  }, [years, selectedYearId]);

  // Fetch performance data
  const { data: performance, isLoading: performanceLoading, refetch } = useQuery({
    queryKey: ['budget-performance', selectedYearId],
    queryFn: () => profitBudgetAPI.getPerformance(selectedYearId).then(res => res.data),
    enabled: !!selectedYearId,
  });

  if (yearsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = performance?.summary || {};
  const accounts = performance?.accounts || [];
  const year = performance?.year || {};

  // Calculate monthly totals
  const monthlyBudgetTotals = MONTHS.map((_, idx) => 
    accounts.reduce((sum, a) => sum + (a.monthly_budget?.[idx + 1] || 0), 0)
  );
  const monthlyActualTotals = MONTHS.map((_, idx) => 
    accounts.reduce((sum, a) => sum + (a.monthly_actual?.[idx + 1] || 0), 0)
  );
  const monthlyVarianceTotals = MONTHS.map((_, idx) => 
    monthlyBudgetTotals[idx] - monthlyActualTotals[idx]
  );

  return (
    <div className="space-y-6" data-testid="budget-performance-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Budget vs Actual Performance</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Compare budgeted amounts against actual revenue from transactions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <YearSelector
            years={years}
            selectedYearId={selectedYearId}
            onSelect={setSelectedYearId}
          />
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {selectedYearId && performance ? (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <SummaryCard
              title="Total Budget"
              value={formatCurrency(summary.total_budget)}
              icon={Target}
              color="text-blue-600"
            />
            <SummaryCard
              title="Total Actual"
              value={formatCurrency(summary.total_actual)}
              icon={DollarSign}
              color="text-green-600"
            />
            <SummaryCard
              title="Variance"
              value={formatCurrency(summary.total_variance)}
              icon={summary.total_variance >= 0 ? TrendingUp : TrendingDown}
              color={summary.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}
              trend={summary.variance_percent}
            />
            <SummaryCard
              title="Achievement Rate"
              value={`${((summary.total_actual / summary.total_budget) * 100 || 0).toFixed(1)}%`}
              subValue={summary.total_actual >= summary.total_budget ? 'On track!' : 'Below target'}
              icon={summary.total_actual >= summary.total_budget ? CheckCircle2 : AlertTriangle}
              color={summary.total_actual >= summary.total_budget ? 'text-green-600' : 'text-yellow-600'}
            />
          </div>

          {/* Variance Summary */}
          <div className={`p-4 rounded-lg border ${
            summary.total_variance >= 0 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-3">
              {summary.total_variance >= 0 ? (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-red-600" />
              )}
              <div>
                <p className={`font-semibold ${summary.total_variance >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                  {summary.total_variance >= 0 
                    ? `You're ${formatCurrency(summary.total_variance)} under budget - Great job!`
                    : `You're ${formatCurrency(Math.abs(summary.total_variance))} over budget`
                  }
                </p>
                <p className={`text-sm ${summary.total_variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  Actual revenue is {formatPercent(Math.abs(summary.variance_percent))} {summary.total_variance >= 0 ? 'below' : 'above'} the budgeted amount for {year.year}
                </p>
              </div>
            </div>
          </div>

          {/* Toggle Monthly View */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showMonthly}
                onChange={(e) => setShowMonthly(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm">Show monthly breakdown</span>
            </label>
          </div>

          {/* Performance Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border bg-muted/30 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Budget vs Actual by Account
              </h3>
              <span className="text-sm text-muted-foreground">
                {accounts.length} revenue sources
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="text-left py-3 px-3 font-medium sticky left-0 bg-muted/50 z-10 min-w-[180px]">
                      Revenue Source
                    </th>
                    <th className="text-right py-3 px-3 font-medium bg-blue-50/50 min-w-[100px]">
                      Budget
                    </th>
                    <th className="text-right py-3 px-3 font-medium bg-green-50/50 min-w-[100px]">
                      Actual
                    </th>
                    <th className="text-right py-3 px-3 font-medium min-w-[120px]">
                      Variance
                    </th>
                    {showMonthly && MONTHS.map((m) => (
                      <th key={m} className="text-center py-3 px-2 font-medium min-w-[90px]">
                        <div>{m}</div>
                        <div className="text-xs text-muted-foreground font-normal">B / A / V</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {performanceLoading ? (
                    <tr>
                      <td colSpan={showMonthly ? 16 : 4} className="py-8 text-center">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </td>
                    </tr>
                  ) : accounts.length > 0 ? (
                    accounts.map((account) => (
                      <PerformanceRow
                        key={account.account_id}
                        account={account}
                        showMonthly={showMonthly}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={showMonthly ? 16 : 4} className="py-8 text-center text-muted-foreground">
                        No budget accounts found. Create budget accounts in Budget Planning first.
                      </td>
                    </tr>
                  )}

                  {/* Totals Row */}
                  {accounts.length > 0 && (
                    <tr className="bg-primary/5 font-semibold border-t-2 border-primary/20">
                      <td className="py-3 px-3 sticky left-0 bg-primary/5 z-10">TOTAL</td>
                      <td className="py-3 px-3 text-right bg-blue-50/50">{formatCurrency(summary.total_budget)}</td>
                      <td className="py-3 px-3 text-right bg-green-50/50">{formatCurrency(summary.total_actual)}</td>
                      <td className={`py-3 px-3 text-right ${
                        summary.total_variance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {summary.total_variance >= 0 ? '+' : ''}{formatCurrency(summary.total_variance)}
                        <span className="text-xs ml-1">({formatPercent(summary.variance_percent)})</span>
                      </td>
                      {showMonthly && MONTHS.map((_, idx) => (
                        <td key={idx} className="py-3 px-2 text-center">
                          <div className="text-xs">
                            <div className="text-muted-foreground">{formatCurrency(monthlyBudgetTotals[idx])}</div>
                            <div className="font-medium">{formatCurrency(monthlyActualTotals[idx])}</div>
                            <div className={monthlyVarianceTotals[idx] >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {monthlyVarianceTotals[idx] >= 0 ? '+' : ''}{formatCurrency(monthlyVarianceTotals[idx])}
                            </div>
                          </div>
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
              <span>Budget (B)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
              <span>Actual (A)</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span>Favorable variance (under budget)</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              <span>Unfavorable variance (over budget)</span>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Budget Year Selected</h3>
          <p className="text-muted-foreground mb-4">
            Select a budget year to view performance comparison, or create one in Budget Planning first.
          </p>
        </div>
      )}
    </div>
  );
}
